import { invoke } from '~/lib/tauriInvoke'
import { listen, type UnlistenFn } from '@tauri-apps/api/event'
import { openUrl } from '@tauri-apps/plugin-opener'
import { isTauriRuntime } from '~/lib/platform/runtime'
import { reactive, toRef } from 'vue'
import { toast } from 'vue-sonner'
import UpdateToast from '~/components/voice-ui/Updater/UpdateToast.vue'

export interface ReleasePayload {
  version: string
  is_downgrade: boolean
}

/** Payload of the macOS-only "update-available" event (src-tauri/src/commands/updates.rs). */
export interface StoreUpdatePayload {
  version: string
  store_url: string
}

let unlisten: UnlistenFn | null = null
let unlistenStore: UnlistenFn | null = null

/**
 * Общее состояние: как только бекенд тихо скачивает обновление (см. spawn_startup_update_check
 * в src-tauri/src/commands/updates.rs), тут выставляется available/version, чтобы кнопка в
 * AppSidebar могла показаться даже после того, как всплывающий тост закрыли.
 */
const state = reactive({
  available: false,
  version: '',
  installing: false,
  /**
   * Set on macOS, where the app can't install updates itself: the Mac App Store delivers them, so
   * the only thing the UI can do is point the user at the store page. Non-empty means "this is a
   * notification, not an installer" — the toast and the sidebar button both switch wording and
   * send the click to `openUrl` instead of `apply_pending_update`.
   */
  storeUrl: '',
})

async function installUpdate() {
  // macOS: nothing to install locally — hand off to the App Store and leave the notification up,
  // since the user updates in the store and relaunches the app themselves.
  if (state.storeUrl) {
    try {
      await openUrl(state.storeUrl)
    } catch (e) {
      console.error('Не удалось открыть App Store:', e)
      toast.error('Не удалось открыть App Store')
    }
    return
  }

  state.installing = true
  try {
    await invoke('apply_pending_update')
  } catch (e) {
    console.error('Не удалось применить обновление:', e)
    state.installing = false
    toast.error('Не удалось установить обновление')
  }
}

/**
 * Слушает событие "update-ready" от Rust-бекенда, предлагает пользователю перезапустить
 * приложение кастомным тостом.
 */
export function useUpdater() {
  async function listenForUpdates() {
    // Auto-update is a desktop-only concept (see `src-tauri/src/commands/updates.rs`) — a browser
    // tab has nothing to listen for here.
    if (!isTauriRuntime() || unlisten || unlistenStore) return

    function showToast(id: string) {
      toast.custom(UpdateToast, {
        id,
        duration: Infinity,
        componentProps: reactive({
          version: toRef(state, 'version'),
          installing: toRef(state, 'installing'),
          storeUrl: toRef(state, 'storeUrl'),
          onInstall: installUpdate,
          onDismiss: () => toast.dismiss(id),
        }),
      })
    }

    // Windows/Linux: Velopack already downloaded the update, the click just restarts into it.
    unlisten = await listen<ReleasePayload>('update-ready', (event) => {
      state.available = true
      state.version = event.payload.version
      state.storeUrl = ''
      showToast(`update-ready-${state.version}`)
    })

    // macOS: a newer version exists on the App Store; the click opens its page.
    unlistenStore = await listen<StoreUpdatePayload>('update-available', (event) => {
      state.available = true
      state.version = event.payload.version
      state.storeUrl = event.payload.store_url
      showToast(`update-available-${state.version}`)
    })
  }

  return { listenForUpdates, updateState: state, installUpdate }
}
